// src/lib/supabase.js
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ─────────────────────────────────────────────
//  IMAGE HELPERS
// ─────────────────────────────────────────────

// Converts a value to a usable image src:
// - Full Supabase URL → returned as-is
// - Legacy filename → prefixed with /assets/
export function getImageUrl(value) {
  if (!value) return '';
  if (value.startsWith('http')) return value;
  // strip any accidental 'assets/' prefix then add /assets/
  return `/assets/${value.replace(/^\/assets\//, '').replace(/^assets\//, '')}`;
}

// Uploads a File object to Supabase Storage.
// Returns the full public URL of the uploaded image.
export async function uploadImage(file) {
  const filename = file.name;

  const { error } = await supabase.storage
    .from('images')
    .upload(filename, file, { upsert: true, contentType: file.type });

  if (error) throw error;

  const { data } = supabase.storage.from('images').getPublicUrl(filename);
  return data.publicUrl;
}

export function getLargeImageUrl(value) {
  if (!value) return '';
  const url = getImageUrl(value);
  return url.replace(/\.webp$/, '-large.webp');
}

// Deletes an image from Supabase Storage by its full URL.
// Safe to call with legacy /assets/ paths — it will just do nothing.
export async function deleteImage(url) {
  if (!url || !url.startsWith('http')) return;
  const filename = url.split('/images/').pop();
  if (!filename) return;
  await supabase.storage.from('images').remove([filename]);
}


// ─────────────────────────────────────────────
//  DATA QUERIES
// ─────────────────────────────────────────────

export const getAbout = async () => {
  const { data, error } = await supabase.from('about').select('*').order('block');
  if (error) throw error;
  return Object.fromEntries(data.map((row) => [row.block, row]));
};

export const getGallery = async () => {
  const { data, error } = await supabase
    .from('gallery')
    .select('id, filename, alt')
    .order('sort_order');
  if (error) throw error;
  return data;
};

export const getTeam = async () => {
  const { data, error } = await supabase
    .from('team')
    .select('slug, name, role, image, bio')
    .order('sort_order');
  if (error) throw error;
  return data;
};

export const getTeamMemberBySlug = async (slug) => {
  const { data, error } = await supabase
    .from('team')
    .select('*')
    .eq('slug', slug)
    .single();
  if (error) throw error;
  return data;
};

export const getClients = async () => {
  const { data, error } = await supabase
    .from('clients')
    .select('name, logo, link')
    .order('sort_order');
  if (error) throw error;
  return data;
};

export const getBlogNews = async () => {
  const { data, error } = await supabase
    .from('blog_news')
    .select('image_url, title, description, article_url')
    .order('sort_order');
  if (error) throw error;
  return data;
};

export const getBlogAudio = async () => {
  const { data, error } = await supabase
    .from('blog_audio')
    .select('image_url, title, description, audio_url, duration')
    .order('sort_order');
  if (error) throw error;
  return data;
};

export const getServiceBySlug = async (slug) => {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('slug', slug)
    .single();
  if (error) throw error;
  return data;
};